import { useState } from 'react';
import axios from 'axios';
import { useStoreState } from 'hooks';
import { useLogin } from 'store/auth';
import { Milestone, Rule, RuleRunStatus } from 'types/dt-graphql-apollo-v2';
import { DATA_VALIDATION_SERVICE_URL, PRIMARY_KEY_FDM } from 'utils/config';
import {
  getSchemaTypeForRule,
  shouldRenewTokenForDV,
} from 'utils/data-validation';
import { flattenJSON } from 'utils/flatten';
import { utils, WorkBook, writeFile } from 'xlsx';

const batchSize = 500000;

function getHeaders(records: Record<string, any>[]): string[] {
  return Array.from(new Set(records.flatMap((r) => Object.keys(r))));
}

export type DownloadReportOptions = {
  collection: Milestone;
  ruleRuns: RuleRunStatus[];
  onError: (err: any) => void;
  onSuccess: (desc: string) => void;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getFileData = async (
  runId: string,
  remainingTries: number,
  token?: string,
  tenant?: string,
  cluster?: string
): Promise<any> => {
  try {
    return await axios({
      method: 'GET',
      url: `${DATA_VALIDATION_SERVICE_URL}/${runId}/failed-instances`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        project: `${tenant}`,
        cluster: `${cluster}`,
      },
    });
  } catch (e) {
    if (remainingTries > 0) {
      await delay(1000); // wait for 1 second before retrying
      return getFileData(runId, remainingTries - 1, token, tenant, cluster);
    }
    return Promise.reject(e);
  }
};
/**
 * Hook that allows the user to download an error report for a collection.
 * For all the rules inside the collection, data validated in the latest run is fetched and stitched together to form an xlsx file.
 *
 * @returns downloadReport: Function that starts the process of downloading an error report.
 * @returns downloadingReport: Boolean value that keeps tracking whether a report is being downloaded or not. */
export const useDownloadReport = (): {
  downloadReport: (options: DownloadReportOptions) => Promise<void>;
  downloadingReport: boolean;
} => {
  let token = useStoreState((state) => state.auth.token);
  const tenant = useStoreState((state) => state.auth.tenant);
  const cluster = useStoreState((state) => state.auth.cluster);
  const expiry = useStoreState((state) => state.auth.expiry);

  const login = useLogin();

  const [downloadingReport, setDownloadingReport] = useState(false);

  const downloadReport = async ({
    collection,
    onError,
    onSuccess,
    ruleRuns,
  }: DownloadReportOptions) => {
    try {
      if (!collection) throw Error('Missing milestone information');
      if (ruleRuns.length === 0) throw Error(`Missing rule runs information`);

      setDownloadingReport(true);
      if (shouldRenewTokenForDV(expiry))
        token = (await login({ tenant, useOIDC: true, forceRefresh: true }))
          .token;

      const { name: collectionName } = collection;
      const runAtTime = ruleRuns[0].runAt;

      const dateTime =
        runAtTime &&
        new Date(runAtTime.toString()).toLocaleDateString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        });

      const errorReportName = `Data validation-${collectionName}-${dateTime}.xlsx`;

      // Start getting data from each run
      const requests = ruleRuns.map((run) =>
        getFileData(run.externalId, 3, token, tenant, cluster)
      );
      const responses = await Promise.all(requests);

      const workbook: WorkBook = utils.book_new();
      let allRows: Object[] = [];

      responses.forEach((response: any, index) => {
        const rule = ruleRuns[index].rule as Rule;
        const {
          customerDefinedId = '',
          errorMessage = 'No error message was set',
        } = rule ?? {};

        const schemaType = getSchemaTypeForRule(rule);
        const failedData: Object[] = response.data;

        if (!failedData) return;

        const entries = flattenJSON(failedData).map((data) => ({
          date: new Date((runAtTime ?? new Date()).toString())
            .toISOString()
            .slice(0, 10),
          errorMessage,
          schemaType,
          customerDefinedId,
          [PRIMARY_KEY_FDM]: data[PRIMARY_KEY_FDM],
          ...data,
        }));
        allRows = allRows.concat(entries);
      });

      // Create one sheet for all data
      if (allRows.length < batchSize) {
        const sheet = utils.json_to_sheet(allRows, {
          header: getHeaders(allRows),
        });
        utils.book_append_sheet(workbook, sheet, 'Validation results');
      }
      // Create a sheet for each batch of data
      else {
        for (let i = 0; i < allRows.length; i += batchSize) {
          const batchedEntries = allRows.slice(i, i + batchSize);

          const sheet = utils.json_to_sheet(batchedEntries, {
            header: getHeaders(allRows),
          });
          utils.book_append_sheet(workbook, sheet, `${i} - ${i + batchSize}`);
        }
      }

      writeFile(workbook, errorReportName);
      onSuccess(
        `Error Report for collection ${collectionName} was successfully downloaded`
      );
    } catch (error: any) {
      onError(error);
    } finally {
      setDownloadingReport(false);
    }
  };
  return { downloadReport, downloadingReport };
};
